// src/pages/public/CategoryPage.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { fetchDesignsCached, fetchDesignsPaginated } from "../../firebase/services";
import { useBranding } from "../../hooks/useBranding";
import { useData } from "../../hooks/useData";
import { ProductCard, ProductModal } from "../../components/ProductCard";
import { B, fonts, Spinner } from "../../components/UI";
import SiteFooter from "../../components/SiteFooter";
import StickyCallButton from "../../components/StickyCallButton";

const SORT_OPTIONS = [
  { value: "newest",    label: "Newest First" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc",label: "Price: High to Low" },
];

const PAGE_SIZE = 20;

export default function CategoryPage() {
  const { slug }         = useParams();
  const navigate         = useNavigate();
  const { branding }     = useBranding();
  const { categories }   = useData();
  const [designs,        setDesigns]    = useState([]);
  const [selected,       setSelected]   = useState(null);
  const [loading,        setLoading]    = useState(true);
  const [loadingMore,    setLoadingMore] = useState(false);
  const [sort,           setSort]       = useState("newest");
  const [filter,         setFilter]     = useState({ badge: "", size: "" });
  const [allDesignsFetched, setAllDesignsFetched] = useState(false);

  // Pagination state
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Sentinel ref for infinite scroll
  const sentinelRef = useRef(null);

  // Separate state for paginated vs full fetched when sort/filter changes
  const [sortFilterChanged, setSortFilterChanged] = useState(false);

  // Initial fetch — paginated by category (no sort filter applies to the raw fetch)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // For "all" slug, fetch across all designs; otherwise filter by category
      const categorySlug = slug === "all" ? null : slug;
      const paginated = await fetchDesignsPaginated(PAGE_SIZE, null, categorySlug);
      if (cancelled) return;
      setDesigns(paginated.designs);
      setLastDoc(paginated.lastDoc);
      setHasMore(paginated.hasMore);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // When sort/filter changes, fetch all designs to apply client-side sort/filter
  useEffect(() => {
    if (!sortFilterChanged) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const allDesigns = await fetchDesignsCached();
      if (cancelled) return;
      setDesigns(allDesigns);
      setAllDesignsFetched(true);
      setHasMore(false);
      setLoading(false);
      setSortFilterChanged(false);
    })();
    return () => { cancelled = true; };
  }, [sortFilterChanged]);

  // Load more designs (infinite scroll)
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || allDesignsFetched) return;
    setLoadingMore(true);
    try {
      const categorySlug = slug === "all" ? null : slug;
      const paginated = await fetchDesignsPaginated(PAGE_SIZE, lastDoc, categorySlug);
      setDesigns(prev => [...prev, ...paginated.designs]);
      setLastDoc(paginated.lastDoc);
      setHasMore(paginated.hasMore);
    } catch (err) {
      console.error("Failed to load more designs:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, allDesignsFetched, lastDoc, slug]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (loading || allDesignsFetched) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "400px 0px" } // fire 400px before reaching the sentinel
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, allDesignsFetched, loadMore]);

  const category = categories.find(c => c.slug === slug);
  const accent   = branding.primaryColor || B.orange;

  // Filter designs for this category
  let filtered = designs.filter(d =>
    d.active && d.visibleOnWebsite === true && (
      slug === "all"
        ? true
        : d.categorySlug === slug || d.categoryId === category?.id
    )
  );

  // Apply badge filter
  if (filter.badge) filtered = filtered.filter(d => d.badge === filter.badge);

  const parsePrice = (p = "") => parseInt(p.replace(/[^\d]/g, "")) || 0;

  // Apply sort
  filtered = [...filtered].sort((a, b) => {
    if (sort === "price-asc")  return parsePrice(a.price) - parsePrice(b.price);
    if (sort === "price-desc") return parsePrice(b.price) - parsePrice(a.price);
    return 0; // newest = default Firestore order
  });

  const handleSortChange = (e) => {
    setSort(e.target.value);
    if (!allDesignsFetched) {
      setSortFilterChanged(true);
    }
  };

  const handleFilterChange = (val) => {
    setFilter(f => ({ ...f, badge: val }));
    if (!allDesignsFetched) {
      setSortFilterChanged(true);
    }
  };

  if (!loading && !category && slug !== "all") {
    return (
      <div style={{ textAlign: "center", padding: "80px 24px" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <h2 style={{ fontFamily: fonts.display, color: B.text }}>Category not found</h2>
        <button onClick={() => navigate("/")} style={{ marginTop: 16, background: accent, color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", cursor: "pointer", fontFamily: fonts.body }}>
          Back to Home
        </button>
      </div>
    );
  }

  const title = category?.name || "All Designs";

  return (
    <>
      <Helmet>
        <title>{title} | {branding.brandName}</title>
        <meta name="description" content={`Shop ${title} at ${branding.brandName}. ${filtered.length} designs available.`} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* ── Hero Banner ── */}
      <div style={{ position: "relative", height: 200, background: `linear-gradient(135deg, ${B.headerBg}, #3D1A00)`, overflow: "hidden", display: "flex", alignItems: "center" }}>
        {category?.bannerUrl && (
          <img src={category.bannerUrl} alt={title}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.3 }} />
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(28,13,0,0.85) 0%, rgba(28,13,0,0.4) 100%)" }} />
        <div style={{ position: "relative", maxWidth: 1280, margin: "0 auto", padding: "0 24px", width: "100%" }}>
          <div style={{ fontSize: 11, color: accent, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8, fontFamily: fonts.body }}>
            <span onClick={() => navigate("/")} style={{ cursor: "pointer", opacity: 0.7 }}>Home</span>
            <span style={{ opacity: 0.4 }}> / </span>
            {title}
          </div>
          <h1 style={{ fontFamily: fonts.display, fontSize: "clamp(28px,5vw,48px)", fontWeight: 700, color: B.white, margin: 0, lineHeight: 1.2 }}>
            {category?.icon && <span style={{ marginRight: 12 }}>{category.icon}</span>}
            {title}
          </h1>
          <p style={{ color: "rgba(255,255,255,0.6)", margin: "8px 0 0", fontSize: 14, fontFamily: fonts.body }}>
            {filtered.length} designs available
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 24px 60px" }}>
        <h2 style={{ margin: "0 0 8px", fontFamily: fonts.display, color: B.text, fontSize: "clamp(22px,3.2vw,30px)" }}>
          Wholesale {title} Collection
        </h2>
        <h3 style={{ margin: "0 0 22px", fontFamily: fonts.body, color: B.textLight, fontSize: 13, fontWeight: 500 }}>
          Explore curated designs and contact our team directly for bulk enquiries.
        </h3>

        {/* ── Filter & Sort Bar ── */}
        <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {/* Badge filters */}
            {[["", "All"], ["new", "New Arrivals"], ["bestseller", "Bestsellers"], ["sale", "Sale"]].map(([val, label]) => (
              <button key={val} onClick={() => handleFilterChange(val)}
                style={{
                  background: filter.badge === val ? accent : B.white,
                  color: filter.badge === val ? "#fff" : B.textMid,
                  border: `1.5px solid ${filter.badge === val ? accent : B.border}`,
                  borderRadius: 50, padding: "6px 16px", fontSize: 12, fontWeight: 600,
                  cursor: "pointer", fontFamily: fonts.body, transition: "all 0.2s",
                }}>
                {label}
              </button>
            ))}
          </div>
          {/* Sort */}
          <select value={sort} onChange={handleSortChange}
            style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, color: B.text, fontFamily: fonts.body, cursor: "pointer", outline: "none" }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* ── Grid ── */}
        {loading ? <Spinner full /> : filtered.length === 0
          ? (
            <div style={{ textAlign: "center", padding: "80px 0", color: B.textLight }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>👗</div>
              <p style={{ fontFamily: fonts.body, fontSize: 15 }}>No designs in this category yet.</p>
              <button onClick={() => navigate("/")}
                style={{ marginTop: 16, background: accent, color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", cursor: "pointer", fontFamily: fonts.body }}>
                Browse All
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 24 }}>
                {filtered.map((d, i) => (
                  <div key={d.id} style={{ animation: `fadeUp 0.45s ${i * 40}ms both` }}>
                    <ProductCard design={d} onClick={setSelected} whatsapp={branding.whatsapp} />
                  </div>
                ))}
              </div>

              {/* ── Infinite scroll sentinel ── */}
              {!loading && !allDesignsFetched && (
                <div ref={sentinelRef} style={{ height: 1, width: "100%" }} />
              )}

              {/* ── Loading more indicator ── */}
              {loadingMore && (
                <div style={{ textAlign: "center", padding: "24px" }}>
                  <Spinner />
                </div>
              )}

              {/* ── End of designs message ── */}
              {!hasMore && !loading && !allDesignsFetched && designs.length > 0 && (
                <div style={{ textAlign: "center", padding: "32px 24px", color: B.textLight, fontFamily: fonts.body, fontSize: 13 }}>
                  — You've seen all designs —
                </div>
              )}
            </>
          )
        }
      </div>

      <SiteFooter branding={branding} categories={categories.filter(c => c.active)} />
      <StickyCallButton phone={branding.phone} />

      <ProductModal design={selected} onClose={() => setSelected(null)} whatsapp={branding.whatsapp} />
    </>
  );
}